/** Reseller dashboard with CDR summary and analytics. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { Cdr } from "../../types/cdr";

interface ResellerDashboardProps {
  resellerId: string;
  resellerAccount: string;
}

interface SummaryStats {
  period: string;
  totalCalls: number;
  totalRevenue: number;
  totalMargin: number;
}

interface TopDestination {
  destination: string;
  callCount: number;
  totalRevenue: number;
}

interface TopCustomer {
  account: string;
  callCount: number;
  totalRevenue: number;
}

/** Reseller dashboard with real-time CDR summary. */
export default function ResellerDashboard({
  resellerId,
  resellerAccount,
}: ResellerDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [statsToday, setStatsToday] = useState<SummaryStats | null>(null);
  const [statsWeek, setStatsWeek] = useState<SummaryStats | null>(null);
  const [statsMonth, setStatsMonth] = useState<SummaryStats | null>(null);
  const [topDestinations, setTopDestinations] = useState<TopDestination[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [recentCdrs, setRecentCdrs] = useState<Cdr[]>([]);

  useEffect(() => {
    loadDashboardData();
    // Refresh every 30 seconds
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, [resellerId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Calculate date ranges
      const today = new Date();
      const todayStart = startOfDay(today);
      const todayEnd = endOfDay(today);
      const weekStart = startOfDay(subDays(today, 7));
      const monthStart = startOfDay(subDays(today, 30));

      // Load summary stats for different periods
      const [todayStats, weekStats, monthStats] = await Promise.all([
        loadStats(todayStart, todayEnd, "Today"),
        loadStats(weekStart, todayEnd, "This Week"),
        loadStats(monthStart, todayEnd, "This Month"),
      ]);

      setStatsToday(todayStats);
      setStatsWeek(weekStats);
      setStatsMonth(monthStats);

      // Load top destinations (this month)
      await loadTopDestinations(monthStart, todayEnd);

      // Load top customers (this month)
      await loadTopCustomers(monthStart, todayEnd);

      // Load recent CDRs
      await loadRecentCdrs();

    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (
    startDate: Date,
    endDate: Date,
    period: string
  ): Promise<SummaryStats> => {
    // Map period to API parameter
    const apiPeriod = period === "Today" ? "today" :
                      period === "This Week" ? "week" : "month";

    const response = await fetch(
      `/api/v1/resellers/me/stats?period=${apiPeriod}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('reseller_token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to load stats');
    }

    const data = await response.json();
    return {
      period,
      totalCalls: data.total_calls,
      totalRevenue: data.total_revenue,
      totalMargin: data.total_margin,
    };
  };

  const loadTopDestinations = async (startDate: Date, endDate: Date) => {
    const startIso = startDate.toISOString();
    const endIso = endDate.toISOString();

    const response = await fetch(
      `/api/v1/resellers/me/top-destinations?limit=10&start_date=${startIso}&end_date=${endIso}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('reseller_token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to load top destinations');
    }

    const data = await response.json();
    // Map API response to frontend interface (camelCase conversion)
    const mapped = data.map((item: any) => ({
      destination: item.destination,
      callCount: item.call_count,
      totalRevenue: item.total_revenue,
    }));
    setTopDestinations(mapped);
  };

  const loadTopCustomers = async (startDate: Date, endDate: Date) => {
    const startIso = startDate.toISOString();
    const endIso = endDate.toISOString();

    const response = await fetch(
      `/api/v1/resellers/me/top-customers?limit=10&start_date=${startIso}&end_date=${endIso}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('reseller_token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to load top customers');
    }

    const data = await response.json();
    // Map API response to frontend interface (camelCase conversion)
    const mapped = data.map((item: any) => ({
      account: item.account,
      callCount: item.call_count,
      totalRevenue: item.total_revenue,
    }));
    setTopCustomers(mapped);
  };

  const loadRecentCdrs = async () => {
    // Use existing /me/cdrs endpoint with limit=10
    const response = await fetch(
      `/api/v1/resellers/me/cdrs?page=1&page_size=10`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('reseller_token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to load recent CDRs');
    }

    const data = await response.json();
    setRecentCdrs(data.cdrs);
  };

  if (loading && !statsToday) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reseller Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Account: {resellerAccount}
      </Typography>

      {/* Summary Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Today's Stats */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Today's Performance
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PhoneIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Total Calls
                </Typography>
              </Box>
              <Typography variant="h4">
                {statsToday?.totalCalls.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <MoneyIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                ${statsToday?.totalRevenue.toFixed(2) || "0.00"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Total Margin
                </Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                ${statsToday?.totalMargin.toFixed(2) || "0.00"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Week Stats */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            This Week
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Calls
              </Typography>
              <Typography variant="h5">
                {statsWeek?.totalCalls.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Revenue
              </Typography>
              <Typography variant="h5">
                ${statsWeek?.totalRevenue.toFixed(2) || "0.00"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Margin
              </Typography>
              <Typography variant="h5">
                ${statsWeek?.totalMargin.toFixed(2) || "0.00"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Month Stats */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            This Month
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Calls
              </Typography>
              <Typography variant="h5">
                {statsMonth?.totalCalls.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Revenue
              </Typography>
              <Typography variant="h5">
                ${statsMonth?.totalRevenue.toFixed(2) || "0.00"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Margin
              </Typography>
              <Typography variant="h5">
                ${statsMonth?.totalMargin.toFixed(2) || "0.00"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Destinations and Customers */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Destinations (This Month)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Destination</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topDestinations.map((dest, index) => (
                    <TableRow key={index}>
                      <TableCell>{dest.destination}</TableCell>
                      <TableCell align="right">{dest.callCount}</TableCell>
                      <TableCell align="right">
                        ${dest.totalRevenue.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Customers (This Month)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topCustomers.map((cust, index) => (
                    <TableRow key={index}>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cust.account}
                      </TableCell>
                      <TableCell align="right">{cust.callCount}</TableCell>
                      <TableCell align="right">
                        ${cust.totalRevenue.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent CDRs */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Recent CDRs
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Call ID</TableCell>
                <TableCell>Caller</TableCell>
                <TableCell>Callee</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Margin</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentCdrs.map((cdr) => (
                <TableRow key={cdr.id}>
                  <TableCell
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      maxWidth: 150,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cdr.call_id}
                  </TableCell>
                  <TableCell>{cdr.caller}</TableCell>
                  <TableCell>{cdr.callee}</TableCell>
                  <TableCell>{cdr.billsec}s</TableCell>
                  <TableCell>
                    {cdr.margin_breakdown
                      ? `$${cdr.margin_breakdown.margin_amount?.toFixed(4)}`
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      color={
                        cdr.hangup_cause === "NORMAL_CLEARING"
                          ? "success.main"
                          : "error.main"
                      }
                    >
                      {cdr.hangup_cause}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
